/**
 * usePushSubscription — abonnement WebPush pour les notifications de chat
 * et alertes système (futur).
 *
 * Le bouton dans Tweaks > Notifications appelle `subscribePush()` :
 *   1. Demande Notification.requestPermission()
 *   2. Demande la VAPID public key au backend (/api/push/vapid-public-key).
 *      Si le backend ne l'expose pas encore (404), on stocke un flag
 *      "demandé mais backend pas prêt" et on s'arrête sans crasher.
 *   3. swReg.pushManager.subscribe({ applicationServerKey, userVisibleOnly })
 *   4. POST la PushSubscription au backend (/api/push/subscribe) pour
 *      qu'il puisse pousser. Si 404, on garde la sub côté navigateur
 *      en attendant l'endpoint serveur.
 *
 * L'état est persisté dans localStorage pour que le toggle Tweaks
 * reflète la réalité au reload, et reagisse à pushsubscriptionchange
 * (le SW poste alors un message "rws.push.resubscribed").
 */

const SUB_KEY = "rws.push.subscribed";
const PERM_KEY = "rws.push.permission"; // cache de la dernière permission vue

export type PushState = "unsupported" | "denied" | "default" | "granted" | "subscribing" | "subscribed";

export function getPushState(): PushState {
  if (typeof window === "undefined") return "unsupported";
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return "unsupported";
  if (!("Notification" in window)) return "unsupported";
  const perm = Notification.permission;
  if (perm === "denied") return "denied";
  if (perm === "default") return "default";
  const subscribed = localStorage.getItem(SUB_KEY) === "1";
  return subscribed ? "subscribed" : "granted";
}

/** Convertit une VAPID public key (base64url) en ArrayBuffer pour
 *  applicationServerKey de PushManager.subscribe. ArrayBuffer plutôt
 *  qu'Uint8Array pour satisfaire le typage BufferSource strict de Lib.dom. */
function b64UrlToBuffer(b64Url: string): ArrayBuffer {
  const padding = "=".repeat((4 - (b64Url.length % 4)) % 4);
  const b64 = (b64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const buf = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return buf;
}

export async function subscribePush(): Promise<{ ok: boolean; reason?: string }> {
  if (getPushState() === "unsupported") return { ok: false, reason: "Push non supporté par ce navigateur." };

  // 1. Permission
  let perm = Notification.permission;
  if (perm === "default") {
    perm = await Notification.requestPermission();
    try {
      localStorage.setItem(PERM_KEY, perm);
    } catch {
      /* ignore */
    }
  }
  if (perm !== "granted") return { ok: false, reason: "Permission refusée." };

  // 2. VAPID public key (backend). Si pas dispo → arrêt propre.
  let vapidKey: string;
  try {
    const r = await fetch("/api/push/vapid-public-key", { credentials: "include" });
    if (!r.ok) return { ok: false, reason: `Backend push pas prêt (HTTP ${r.status}).` };
    const j = (await r.json()) as { publicKey?: string };
    if (!j.publicKey) return { ok: false, reason: "Backend push : publicKey absente." };
    vapidKey = j.publicKey;
  } catch (err) {
    return { ok: false, reason: `Backend push injoignable : ${err instanceof Error ? err.message : String(err)}` };
  }

  // 3. Subscribe via le SW
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64UrlToBuffer(vapidKey),
    });
  }

  // 4. Envoie au backend
  try {
    const r = await fetch("/api/push/subscribe", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(sub.toJSON()),
    });
    if (!r.ok && r.status !== 404) {
      // 404 acceptable (endpoint pas encore implémenté) — on garde la sub locale.
      return { ok: false, reason: `Backend a refusé l'inscription (HTTP ${r.status}).` };
    }
  } catch {
    /* tolère : la sub reste locale, à re-pousser plus tard. */
  }

  try {
    localStorage.setItem(SUB_KEY, "1");
  } catch {
    /* ignore */
  }
  return { ok: true };
}

export async function unsubscribePush(): Promise<{ ok: boolean }> {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await sub.unsubscribe();
      try {
        await fetch("/api/push/unsubscribe", {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
      } catch {
        /* tolère 404 */
      }
    }
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(SUB_KEY);
  } catch {
    /* ignore */
  }
  return { ok: true };
}

/**
 * Branche un listener sur les messages postMessage du SW (résubscribe
 * après pushsubscriptionchange, ou clic notification → naviguer).
 * À appeler une fois au boot.
 */
export function installPushMessageListener(onClick: (data: Record<string, unknown>, action: string) => void) {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return () => {};
  const handler = async (e: MessageEvent) => {
    const msg = e.data;
    if (!msg || typeof msg !== "object") return;
    if (msg.type === "rws.notification.click") {
      onClick((msg.data || {}) as Record<string, unknown>, msg.action || "open");
    } else if (msg.type === "rws.push.resubscribed" && msg.subscription) {
      // Re-poste la nouvelle sub au backend.
      try {
        await fetch("/api/push/subscribe", {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(msg.subscription),
        });
      } catch {
        /* tolère */
      }
    }
  };
  navigator.serviceWorker.addEventListener("message", handler);
  return () => navigator.serviceWorker.removeEventListener("message", handler);
}
